'use client';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { trackRequestCreated } from '@/lib/analytics';
import { notify } from '@/lib/toast';
import { X, ChevronRight, ChevronLeft, Clock, Calendar, MessageSquare } from 'lucide-react';

const URGENCY_OPTIONS = [
  { value: 'urgent',      label: 'Срочно',          hint: 'Сегодня или завтра',    emoji: '🔴' },
  { value: 'this_week', label: 'На этой неделе',  hint: 'В ближайшие 7 дней',   emoji: '🟡' },
  { value: 'this_month',label: 'В течение месяца',hint: 'Не горит, но скоро',   emoji: '🟢' },
  { value: 'flexible',  label: 'Дата не важна',   hint: 'Когда удобно мастеру', emoji: '⚪' },
];

const CATEGORY_ICONS: Record<string, string> = {
  it: '💻', design: '🎨', marketing: '📣', copywriting: '✍️',
  education: '📚', cleaning: '🧹', repair: '🔧', beauty: '💅',
  health: '🏥', fitness: '💪', photo: '📷', legal: '⚖️',
  accounting: '📊', transport: '🚚', events: '🎉', pets: '🐾',
  food: '🍽️', handmade: '🧶', auto: '🚗', other: '📋',
};

export default function NewRequestForm({
  onClose,
  onSuccess,
  defaultCategoryId,
}: {
  onClose: () => void;
  onSuccess?: () => void;
  defaultCategoryId?: string;
}) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(defaultCategoryId ? 2 : 1);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    category_id: defaultCategoryId || '',
    title: '',
    description: '',
    budget: '',
    urgency: 'flexible',
    preferred_date: '',
    time_comment: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get('/categories').then(r => r.data.data as any[]),
  });

  const set = (field: string, value: string) => {
    setForm(f => ({ ...f, [field]: value }));
    setErrors(e => ({ ...e, [field]: '' }));
  };

  const validateStep1 = () => {
    if (!form.category_id) {
      setErrors({ category_id: 'Выберите категорию' });
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    const e: Record<string, string> = {};
    if (!form.title.trim() || form.title.trim().length < 3) e.title = 'Минимум 3 символа';
    if (!form.budget || Number(form.budget) < 1) e.budget = 'Укажите бюджет (минимум 1 ₽)';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goNext = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    setStep(s => s + 1);
  };
  const goBack = () => setStep(s => s - 1);

  const onSubmit = async () => {
    if (!validateStep2()) { setStep(2); return; }
    setSubmitting(true);
    try {
      await api.post('/requests', {
        category_id: form.category_id,
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        budget: Number(form.budget),
        urgency: form.urgency,
        preferred_date: form.preferred_date || undefined,
        time_comment: form.time_comment.trim() || undefined,
      });
      queryClient.invalidateQueries({ queryKey: ['my-requests'] });
      notify.success('Заказ опубликован!');
      trackRequestCreated();
      onSuccess?.();
      onClose();
    } catch (err) {
      notify.apiError(err, 'Ошибка создания заказа');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCategory = categories.find((c: any) => c.id === form.category_id);
  const TOTAL_STEPS = 3;

  return (
    <div className="bg-bg-surface rounded-2xl border border-border p-6">
      {/* Заголовок */}
      <div className="flex justify-between items-start mb-5">
        <div>
          <h2 className="text-lg font-bold text-text-primary">Новый заказ</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Шаг {step} из {TOTAL_STEPS} —{' '}
            {step === 1 ? 'Выберите категорию' : step === 2 ? 'Опишите задачу' : 'Когда нужен специалист'}
          </p>
          <div className="flex gap-1 mt-2">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <div key={i} className={`h-1 w-8 rounded-full transition-colors ${i + 1 === step ? 'bg-brand' : i + 1 < step ? 'bg-brand/50' : 'bg-gray-200'}`} />
            ))}
          </div>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-text-secondary transition-colors mt-1">
          <X size={20} />
        </button>
      </div>

      {/* ШАГ 1 — Категория */}
      {step === 1 && (
        <div className="space-y-4">
          {errors.category_id && (
            <p className="text-xs text-red-500 bg-red-50 rounded-lg px-3 py-2">{errors.category_id}</p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1">
            {categories.filter((c: any) => c.isActive && !c.deletedAt).map((cat: any) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => { set('category_id', cat.id); setStep(2); }}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 text-center transition-all hover:border-brand hover:bg-brand/5 ${
                  form.category_id === cat.id
                    ? 'border-brand bg-brand/5'
                    : 'border-border bg-bg-surface'
                }`}
              >
                <span className="text-2xl">{CATEGORY_ICONS[cat.id] || '📋'}</span>
                <span className="text-xs font-medium text-text-ui leading-tight">{cat.name}</span>
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 text-center">Нажмите на категорию — перейдёте к следующему шагу</p>
        </div>
      )}

      {/* ШАГ 2 — Описание */}
      {step === 2 && (
        <div className="space-y-4">
          {selectedCategory && (
            <div className="flex items-center gap-2 bg-brand/5 rounded-xl px-3 py-2">
              <span className="text-lg">{CATEGORY_ICONS[selectedCategory.id] || '📋'}</span>
              <span className="text-sm font-medium text-brand">{selectedCategory.name}</span>
              <button type="button" onClick={() => setStep(1)} className="ml-auto text-xs text-gray-400 hover:text-text-secondary">изменить</button>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-text-ui mb-1.5">
              Название задачи <span className="text-red-500">*</span>
            </label>
            <input
              value={form.title}
              onChange={e => set('title', e.target.value)}
              placeholder="Например: Уборка двухкомнатной квартиры"
              className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand transition-colors ${errors.title ? 'border-red-400' : 'border-border-input'}`}
            />
            {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-ui mb-1.5">
              Описание <span className="text-gray-400 font-normal">— необязательно</span>
            </label>
            <textarea
              value={form.description}
              onChange={e => set('description', e.target.value)}
              rows={3}
              placeholder="Опишите детали задачи — это поможет исполнителям лучше оценить объём работы"
              className="w-full border border-border-input rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand resize-none transition-colors"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{form.description.length} символов</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-ui mb-1.5">
              Бюджет (₽) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={form.budget}
              onChange={e => set('budget', e.target.value)}
              placeholder="Например: 3000"
              className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand transition-colors ${errors.budget ? 'border-red-400' : 'border-border-input'}`}
            />
            {errors.budget && <p className="text-xs text-red-500 mt-1">{errors.budget}</p>}
          </div>

          <div className="flex justify-between pt-1">
            <button type="button" onClick={goBack}
              className="flex items-center gap-2 border border-border-input text-text-secondary px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-bg-subtle transition-colors">
              <ChevronLeft size={16} /> Назад
            </button>
            <button type="button" onClick={goNext}
              className="flex items-center gap-2 bg-brand text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-brand-dark transition-all">
              Далее <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* ШАГ 3 — Время */}
      {step === 3 && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-ui mb-2">
              <Clock size={14} className="inline mr-1.5 text-brand" />
              Срочность
            </label>
            <div className="grid grid-cols-2 gap-2">
              {URGENCY_OPTIONS.map(opt => (
                <button key={opt.value} type="button"
                  onClick={() => set('urgency', opt.value)}
                  className={`text-left p-3 rounded-xl border-2 transition-all ${form.urgency === opt.value ? 'border-brand bg-brand/5' : 'border-border hover:border-border-input bg-bg-surface'}`}>
                  <div className="text-sm font-medium text-text-body">{opt.emoji} {opt.label}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{opt.hint}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-ui mb-1.5">
              <Calendar size={14} className="inline mr-1.5 text-brand" />
              Желаемая дата <span className="text-gray-400 font-normal">— необязательно</span>
            </label>
            <input type="date"
              value={form.preferred_date}
              onChange={e => set('preferred_date', e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full border border-border-input rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand transition-colors" />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-ui mb-1.5">
              <MessageSquare size={14} className="inline mr-1.5 text-brand" />
              Уточнение по времени <span className="text-gray-400 font-normal">— необязательно</span>
            </label>
            <input
              value={form.time_comment}
              onChange={e => set('time_comment', e.target.value)}
              placeholder="Например: только по вечерам после 18:00"
              className="w-full border border-border-input rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand transition-colors" />
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={goBack}
              className="flex items-center gap-2 border border-border-input text-text-secondary px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-bg-subtle transition-colors">
              <ChevronLeft size={16} /> Назад
            </button>
            <button type="button" onClick={onSubmit} disabled={submitting}
              className="flex-1 bg-brand text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-brand-dark transition-all disabled:opacity-50">
              {submitting ? 'Публикуем...' : 'Опубликовать заказ'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
